import type { LoaderFunctionArgs } from '~/types/route-args';
import type { MetaDescriptor } from 'react-router';
import { redirect, useLoaderData } from 'react-router';
import invariant from 'tiny-invariant';
import { B2BIndexPage } from '@modules/b2b/B2BIndexPage';
import {
    buildB2BRootLoaderData,
    isB2BRootLoaderData,
} from '~/utils/business-subdomain';
import { getHost, isBusinessHost } from '~/utils/request.server';
import { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { createOrganisationSchema } from '~/modules/seo/schema/organisation-schema';
import { createWebsiteSchema } from '~/modules/seo/schema/website-schema';
import { createLDSchema } from '~/modules/seo/util/create-ld-schema';
import { getMetaBaseUrl } from '~/modules/seo/util/create-meta-base-url';
import {
    createMetaTitle,
    createMetaDescription,
    getCanonicalHreflang,
    createOGTags,
    mergeMeta,
} from '@modules/seo';
import { getRouteData } from '~/modules/root/util/get-route-data';
import type { APHandle } from '@modules/root';
import { buildLoggedOutHomepageData } from '@modules-rocco/home/api/build-logged-out-homepage-data.server';

import { HomePage } from '@ui-rocco/pages/home-page/HomePage';
import { useHomePageTracking } from '~/ui-rocco/hooks/use-home-page-tracking';
import React from 'react';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';

/**
 * The homepage uses the unified header. The tall hero-overlay layout
 * (`showLogomark`) renders the large logomark + vertical nav stack over the
 * dark hero; `pageTheme: 'transparent'` already maps to the `b2bTransparent`
 * colour variant in AppPage. No tagline/CTA overrides — the header's rich
 * default tagline and standard Log In / Sign Up cluster match the design.
 */
export const handle: APHandle = {
    header: {
        headerVariant: 'b2bTransparent',
        showLogomark: true,
    },
};

export const meta = mergeMeta<typeof loader>(
    () => {
        return [];
    },
    (props) => {
        const { matches, data } = props;

        // Business subdomain root serves the B2B index page from this same
        // route. It needs its own title/description/canonical (the consumer
        // homepage schema below does not apply).
        if (isB2BRootLoaderData(data)) {
            const b2bTitle = createMetaTitle(props, [
                'ArchiPro Business',
                'ArchiPro',
            ]);
            const b2bDescription = createMetaDescription([
                'Where architecture and design gets discovered. And decided. ArchiPro connects brands, architects, designers and the homeowners shaping every project.',
            ]);
            return [
                { title: b2bTitle },
                { name: 'description', content: b2bDescription },
                {
                    tagName: 'link',
                    rel: 'canonical',
                    href: `${data.businessOrigin}/`,
                },
            ];
        }

        const title = createMetaTitle(
            props,
            [data?.localisation?.Title],
            false,
        );
        const description = createMetaDescription([
            data?.localisation?.Description,
        ]);

        const { rootData } = getRouteData(matches);

        const baseUrl = getMetaBaseUrl(props);
        invariant(
            rootData?.localisation.country,
            'index.ts meta(): country is required',
        );

        const organisationSchema = createOrganisationSchema(props);
        const websiteSchema = createWebsiteSchema(baseUrl);

        const ldSchema = createLDSchema([
            organisationSchema ?? [],
            websiteSchema ?? [],
        ]);

        // Temporarily remove preloading until structure is confirmed
        const preloadImageLinks: MetaDescriptor[] = [];

        return [
            { title },
            { name: 'description', content: description },
            ...createOGTags({
                meta: props,
                title,
                description,
            }),
            // The NZ/AU/US market homepages are language-identical equivalents
            // of one another, so advertise the full market cluster + x-default
            // rather than a self-only canonical. The same code runs on each
            // apex host, so the cluster is reciprocal; this lets Google serve
            // the US homepage to US users instead of consolidating brand
            // queries onto the established NZ/AU sites.
            ...getCanonicalHreflang(props, { forceAllRegions: true }),
            ...ldSchema,
            ...preloadImageLinks,
        ];
    },
);

/** Consumer (apex host) homepage data fetch — the logged-out home page. */
const loadConsumerHomePage = async (args: LoaderFunctionArgs) => {
    const { context } = args;
    const isLoggedIn = context.user.__typename === 'Me';

    if (isLoggedIn) {
        return redirect('/app');
    }

    const localisation = await getPageLocalisation(args);
    const roccoHomePageData = await buildLoggedOutHomepageData(args);

    return {
        deviceType: context.device.type,
        localisation,
        ...roccoHomePageData,
        isLoggedIn,
        pageTheme: 'transparent',
    };
};

export const loader = async (args: LoaderFunctionArgs) => {
    // Business subdomain (e.g. business.archiproamerica.com/) serves the B2B
    // index page at the root path. Branch first so it takes precedence over
    // the logged-in redirect and the consumer homepage data fetch. The B2B
    // payload is cast to the consumer data type so this route's `typeof loader`
    // stays consumer-shaped for the many home-page components that read it; the
    // B2B branch is recovered at runtime via the `b2b` marker (isB2BRootLoaderData).
    if (isBusinessHost(args.request)) {
        return buildB2BRootLoaderData(
            getHost(args.request),
        ) as unknown as Awaited<ReturnType<typeof loadConsumerHomePage>>;
    }

    return loadConsumerHomePage(args);
};

const LoggedOutHomePage = () => {
    useHomePageTracking();

    const { setCategory } = useCurrentCategory();

    React.useEffect(() => {
        setCategory('All');
        return () => setCategory(null);
    }, [setCategory]);

    return <HomePage />;
};

const HomeRoute = () => {
    const data = useLoaderData<typeof loader>();
    if (isB2BRootLoaderData(data)) {
        return <B2BIndexPage mainSiteOrigin={data.mainSiteOrigin} />;
    }
    return <LoggedOutHomePage />;
};

export default HomeRoute;
